import React, { useState, useEffect } from 'react';
import ChatWindow from '../components/features/chat/ChatWindow/ChatWindow';
import HeroBannerSkeleton from '../components/shared/HeroBannerSkeleton';
import axios from 'axios';
import { Box, Container, useMediaQuery, useTheme, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import HeroBanner from '../components/features/chat/HeroBanner/HeroBanner';
import { useHeroBannerData } from '../hooks/useHeroBannerData';
import { usePageTitle } from '../hooks/usePageTitle';

function PersonalizedPage({ subdomain }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [userPhoto, setUserPhoto] = useState(null);
  const [userName, setUserName] = useState(null);
  const [workspaceName, setWorkspaceName] = useState(null);
  const [error, setError] = useState(null);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);

  usePageTitle(
    workspaceName ? `${workspaceName}` : 'Personalized Page',
    'Get To Know Me'
  );

  // Utilisation du hook personnalisé
  const { heroBannerEditableData, setHeroBannerEditableData, isDataLoaded } =
    useHeroBannerData(workspaceData);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setError(null);

        const workspaceResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/public/user-by-workspace-subdomain/${subdomain}`
        );
        const workspaceUserId = workspaceResponse.data.userId;
        const workspaceName = workspaceResponse.data.workspaceName;
        setWorkspaceName(workspaceName);
        setWorkspaceId(workspaceResponse.data.workspaceId);

        const photoResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/public/user-photo/${workspaceUserId}`
        );
        setUserPhoto(photoResponse.data.photoURL);
        setUserName(photoResponse.data.userName);
        // Mettre à jour les données du workspace pour déclencher le hook
        setWorkspaceData(workspaceResponse.data.heroBannerData);
      } catch (error) {
        console.error(
          'Erreur lors de la récupération des données utilisateur:',
          error
        );
        setError(t('personalizedPage.errorLoadingProfile'));
      }
    };

    if (subdomain) {
      fetchUserData();
    }
  }, [subdomain, t]);

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {isDataLoaded ? (
        <HeroBanner
          userPhoto={userPhoto}
          userName={userName}
          workspaceName={workspaceName}
          previewMode={false}
          heroBannerEditableData={heroBannerEditableData}
          setHeroBannerEditableData={setHeroBannerEditableData}
        />
      ) : (
        <HeroBannerSkeleton variant="page" />
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            width: '100%',
            mb: isMobile ? 2 : 3,
            mx: isMobile ? 1 : 0,
          }}
        >
          {error}
        </Alert>
      )}

      <Container maxWidth="lg" sx={{ pb: isMobile ? 1 : 4 }}>
        <Box
          sx={{
            mt: isMobile ? 0.5 : 2,
            borderRadius: isMobile ? 3 : 5,
            boxShadow: isMobile
              ? '0 1px 5px rgba(0, 0, 0, 0.1)'
              : '0 2px 10px rgba(0, 0, 0, 0.1)',
            alignItems: 'stretch',
            minHeight: isMobile ? '70vh' : 'auto',
            height: isMobile ? '75vh' : 'auto',
          }}
        >
          <ChatWindow
            subdomain={subdomain}
            userPhoto={userPhoto}
            workspaceId={workspaceId}
          />
        </Box>
      </Container>
    </Box>
  );
}

export default PersonalizedPage;
